import React from "react";
import { Icon } from "../icons";
import { fmtDate, fmtDateTime, todayStr } from "../lib/format";
import { findJob, stageMissingItems, unreviewedTexts } from "../lib/selectors";
import { getApiBase } from "../../../business/redux/reducers/mcOpsDashboard/apiBase";
import { useMcOps } from "../lib/McOpsContext";

const CHIPS = [
  ["all", "All"],
  ["missed-call", "Calls"],
  ["text", "Text"],
];

const channelLabel = (kind, channel) =>
  kind === "stage"
    ? "Zoho stage"
    : kind === "escalation"
    ? "Internal"
    : {
        "missed-call": "Call",
        voicemail: "Voicemail",
        "outbound-call": "Call",
        "inbound-call": "Call",
        text: "Text",
        email: "Email",
      }[channel] || channel;

const channelColor = (kind, channel) =>
  kind === "stage"
    ? "var(--warning)"
    : kind === "escalation"
    ? "var(--purple)"
    : channel === "missed-call" || channel === "voicemail"
      ? "var(--danger)"
      : channel === "text"
        ? "var(--cyan)"
        : channel === "email"
          ? "var(--indigo)"
          : "var(--ink-500)";

const actionLabel = (kind, channel) =>
  kind === "stage"
    ? "Open job"
    : kind === "escalation"
    ? "Resolve"
    : ["missed-call", "voicemail", "outbound-call", "inbound-call"].includes(channel)
      ? "Call now"
      : "Reply";

const CALL_CHANNELS = ["missed-call", "voicemail", "outbound-call", "inbound-call"];

// Items waiting longer than these show again in their own columns (Miles, Asana 1220099429071097).
const OVER_24H = 24 * 60;
const OVER_48H = 48 * 60;

// "1 h 20 min ago" from ageMinutes when the backend sends it, else from the date.
export const fmtAge = (ageMinutes, date) => {
  let mins = Number(ageMinutes);
  if (ageMinutes == null || ageMinutes === "" || Number.isNaN(mins)) {
    const t = new Date(date).getTime();
    if (!date || Number.isNaN(t)) return "";
    mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  }
  mins = Math.max(0, Math.round(mins));
  if (mins < 1) return "just now";
  if (mins < 60) return mins + " min ago";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return h + " h" + (m ? " " + m + " min" : "") + " ago";
  const d = Math.floor(h / 24);
  return d + " d" + (h % 24 ? " " + (h % 24) + " h" : "") + " ago";
};

// The backend origin the dashboard talks to: the API base without its "/api".
const backendBaseUrl = () => getApiBase().replace(/\/api\/?$/, "").replace(/\/$/, "");

/** Minutes the item has waited: ageMinutes from the backend, else from its date; null when unknown. */
export const ageOf = (item, now = Date.now()) => {
  const mins = Number(item.ageMinutes);
  if (item.ageMinutes != null && item.ageMinutes !== "" && !Number.isNaN(mins)) return mins;
  const t = new Date(item.date).getTime();
  return item.date && !Number.isNaN(t) ? Math.max(0, Math.round((now - t) / 60000)) : null;
};

/** The unanswered calls and texts: the past week when the backend sends it, else the shorter feeds. */
const unansweredFeed = (state) => state.unansweredOpen || state.unanswered48h || state.unanswered24h || {};

/**
 * Every open item, newest first: client calls to return and unanswered texts
 * (live from the backend, so a row clears itself once the MC calls or texts
 * back), dashboard notifications, open escalations and Zoho stage flags.
 */
export const needsAttentionItems = (state, naFilter = "all") => {
  const feed = unansweredFeed(state);
  const unanswered = (feed.calls || [])
    .concat(unreviewedTexts(state, feed.texts))
    .map((i) => ({ ...i, kind: "unanswered" }));
  const ext = (state.notifExternal || [])
    .filter((n) => n.unattended)
    .map((n) => ({ id: n.id, kind: "external", channel: n.channel, text: n.text, date: n.date, jobId: n.jobId }));
  const esc = (state.escalations || [])
    .filter((e) => e.status === "open")
    .map((e) => ({
      id: e.id,
      kind: "escalation",
      channel: "internal",
      text: (e.type === "ops" ? "Ops" : "Sales") + " escalation: " + e.note,
      date: e.createdAt,
      jobId: e.jobId,
    }));
  let items = unanswered
    .concat(ext, esc, stageMissingItems(state))
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  if (naFilter !== "all") {
    items = items.filter((i) =>
      naFilter === "internal"
        ? i.channel === "internal"
        : naFilter === "missed-call"
          ? CALL_CHANNELS.includes(i.channel)
          : i.channel === naFilter,
    );
  }
  return items;
};

/** One item: its tag, the client, the text, when, and the action that clears it. */
const ItemRow = ({ item: i, state, actions, attendItem }) => {
  const job = findJob(state, i.jobId);
  const age = fmtAge(i.ageMinutes, i.date);

  const openReply = () => {
    window.open(`${backendBaseUrl()}/conversation/leads/${job.leadId}`, "_blank", "noopener");
    attendItem(i.id, "unanswered", i.jobId);
  };
  const callNow = () => {
    if (job && actions && actions.callClient) actions.callClient(job.id);
    attendItem(i.id, "unanswered", i.jobId);
  };

  let buttons;
  if (i.kind === "unanswered" && i.channel === "text") {
    buttons = (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <button
          className="btn small primary"
          disabled={!job || !job.leadId}
          title={job && job.leadId ? "Open the conversation" : "No Zoho lead"}
          onClick={openReply}
        >
          Reply
        </button>
        {i.canReview ? (
          <button
            className="btn small"
            title="Short reply that needs no action — mark as reviewed"
            onClick={() => actions && actions.markTextReviewed && actions.markTextReviewed(i.jobId, i.messageId, i.text)}
          >
            Reviewed
          </button>
        ) : null}
      </div>
    );
  } else if (i.kind === "unanswered") {
    buttons = (
      <button className="btn small primary" onClick={callNow}>
        Call now
      </button>
    );
  } else {
    buttons = (
      <button className="btn small primary" onClick={() => attendItem(i.id, i.kind, i.jobId)}>
        {actionLabel(i.kind, i.channel)}
      </button>
    );
  }

  const tagKind = i.kind === "unanswered" ? "external" : i.kind;
  return (
    <div
      className="note-item"
      data-testid="na-item"
      style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <span
          className="badge plain"
          style={{ background: channelColor(tagKind, i.channel), color: "#fff", border: "none", marginRight: 6 }}
        >
          {channelLabel(tagKind, i.channel)}
        </span>
        {job ? <strong>{job.clientName}</strong> : null}
        <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{i.text}</div>
        <div className="meta">
          {fmtDateTime(i.date)}
          {age ? " · " + age : ""}
        </div>
      </div>
      {buttons}
    </div>
  );
};

/** A column of items: the whole feed, or what has waited over 24 h / 48 h. */
const ItemColumn = ({ title, icon, items, empty, rowProps, testId }) => (
  <div className="panel" data-testid={testId}>
    {title ? (
      <div className="panel-head">
        {icon ? <Icon name={icon} /> : null}
        {title}
        <span className="count" style={{ marginLeft: "auto" }}>
          {items.length}
        </span>
      </div>
    ) : null}
    <div className="panel-body" style={{ maxHeight: "none" }}>
      {items.length ? (
        items.map((i) => <ItemRow key={i.kind + "-" + i.id} item={i} {...rowProps} />)
      ) : (
        <div className="panel-empty">{empty}</div>
      )}
    </div>
  </div>
);

export const NeedsAttentionPage = () => {
  const { state, actions, naFilter, setNaFilter, attendItem, completeReminder } = useMcOps();

  // Everything is listed first; what has waited over 24 h and over 48 h shows again in its own column.
  const items = needsAttentionItems(state, naFilter);
  const oldestFirst = (list) => list.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
  const over48 = oldestFirst(items.filter((i) => ageOf(i) >= OVER_48H));
  const over24 = oldestFirst(items.filter((i) => ageOf(i) >= OVER_24H && ageOf(i) < OVER_48H));
  const allCount = needsAttentionItems(state).length;
  const rowProps = { state, actions, attendItem };

  const overdue = (state.reminders || [])
    .filter((r) => r.date < todayStr() && !r.completed)
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <div className="page active">
      <div className="page-title-row">
        <div>
          <h2 className="page-title">
            Needs Attention{" "}
            <span className="meta" style={{ fontWeight: 800, color: "var(--danger)" }}>
              {allCount ? "· " + allCount : ""}
            </span>
          </h2>
          <div className="page-desc">
            Every missed call, unanswered text or email, and open escalation across all jobs — in one feed,
            so nothing slips through. Anything waiting over 24 hours or over 48 hours also shows in its own
            column. One click clears an item and opens the job.
          </div>
        </div>
      </div>

      <div className="chip-row">
        {CHIPS.map(([key, label]) => (
          <button
            type="button"
            className={`chip${naFilter === key ? " active" : ""}`}
            key={key}
            onClick={() => setNaFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="na-grid">
        <ItemColumn testId="na-all" items={items} empty="Nothing needs attention right now." rowProps={rowProps} />
        <ItemColumn
          testId="na-over-24"
          title="Over 24 h"
          icon="clock"
          items={over24}
          empty="Nothing waiting over 24 hours."
          rowProps={rowProps}
        />
        <ItemColumn
          testId="na-over-48"
          title="Over 48 h"
          icon="alert"
          items={over48}
          empty="Nothing waiting over 48 hours."
          rowProps={rowProps}
        />

        <div className="panel">
          <div className="panel-head">Overdue Reminders</div>
          <div className="panel-body" style={{ maxHeight: "none" }}>
            {overdue.length ? (
              overdue.map((r) => (
                <div
                  className="reminder-item"
                  key={r.id}
                  style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}
                >
                  <div>
                    {r.text}
                    <div className="meta">
                      {r.jobLabel ? r.jobLabel + " · " : ""}
                      {fmtDate(r.date)}
                    </div>
                  </div>
                  <button className="btn small" onClick={() => completeReminder(r.id)}>
                    <Icon name="check" />
                    Done
                  </button>
                </div>
              ))
            ) : (
              <div className="panel-empty">No overdue reminders.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
